import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import './BookDetail.css';
import { notify } from '../utils/notify';
import { API_BASE } from '../config/api';
import Ads from './Ads';

export default function BookDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [subscribed, setSubscribed] = useState(false);
  const [publisherEmail, setPublisherEmail] = useState('');
  const [showScrollHint, setShowScrollHint] = useState(true);

  const getCommentAuthorName = (commentItem) => {
    if (!commentItem) return 'Utilisateur';
    return (
      commentItem.full_name ||
      commentItem.user_name ||
      commentItem.username ||
      commentItem.name ||
      (commentItem.user_email ? commentItem.user_email.split('@')[0] : 'Utilisateur')
    );
  };

  const getCommentAuthorProfile = (commentItem) => {
    if (!commentItem) return 'uploads/profiles/default-profile.png';
    return (
      commentItem.profile_picture ||
      commentItem.user_profile ||
      commentItem.profile ||
      commentItem.profile_path ||
      commentItem.profile_image ||
      commentItem.avatar ||
      'uploads/profiles/default-profile.png'
    );
  };

  const toProfileUrl = (profilePath) => {
    if (!profilePath) return `${API_BASE}/uploads/profiles/default-profile.png`;
    if (/^https?:\/\//i.test(profilePath)) return profilePath;
    return `${API_BASE}/${profilePath}`;
  };

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/books/${id}`);
        setBook(res.data);
        setPublisherEmail(String(res.data?.publisher_email || '').trim().toLowerCase());

        await axios.put(`${API_BASE}/api/books/${id}/view`, {
          user_email: user?.email
        });

        const commentsRes = await axios.get(`${API_BASE}/api/comments/${id}`);
        setComments(commentsRes.data);

        if (user) {
          const subRes = await axios.get(`${API_BASE}/api/subscriptions`, {
            params: { user_email: user.email, author_id: res.data.author_id }
          });
          setSubscribed(subRes.data.subscribed);
        }
      } catch (err) {
        console.error('Erreur chargement livre:', err);
      }
    };

    fetchBook();
  }, [id, user]);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 120) {
        setShowScrollHint(false);
      } else {
        setShowScrollHint(true);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLike = async () => {
    try {
      let guestToken = localStorage.getItem('booktubeGuestLikeToken');
      if (!user?.email && !guestToken) {
        guestToken = (window.crypto?.randomUUID?.() || `guest-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
        localStorage.setItem('booktubeGuestLikeToken', guestToken);
      }

      await axios.put(`${API_BASE}/api/books/${id}/like`, {
        user_email: user?.email || '',
        guest_token: user?.email ? '' : (guestToken || '')
      });
      setBook((prev) => ({ ...prev, likes: (prev?.likes || 0) + 1 }));
    } catch (err) {
      notify(err.response?.data?.error || 'Erreur lors du like');
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareData = {
      title: book.title,
      text: `Decouvrez ce livre: ${book.title}`,
      url: shareUrl
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        notify('Lien copie dans le presse-papiers.');
        return;
      }
      window.prompt('Copiez ce lien :', shareUrl);
    } catch (err) {
      if (err?.name !== 'AbortError') notify('Impossible de partager le lien pour le moment.');
    }
  };

  const handleComment = async () => {
    if (!user || !comment || !book?.id) return;
    try {
      const res = await axios.post(`${API_BASE}/api/comments`, {
        book_id: book.id,
        user_email: user.email,
        comment
      });

      setComments((prev) => [{
        ...res.data,
        user_email: user.email,
        full_name: user.full_name || user.name || user.username || (user.email ? user.email.split('@')[0] : 'Utilisateur'),
        profile_picture: user.profile_picture || user.profile || user.profile_path || user.profile_image || user.avatar || 'uploads/profiles/default-profile.png',
        user_profile: user.profile || user.profile_path || user.profile_image || user.avatar || 'uploads/profiles/default-profile.png'
      }, ...prev]);
      setComment('');
    } catch (err) {
      notify(err.response?.data?.error || "Erreur lors de l'envoi du commentaire");
    }
  };

  const handleSubscribe = async () => {
    if (!user || !book) return;
    try {
      if (!subscribed) {
        await axios.post(`${API_BASE}/api/subscribe`, {
          user_email: user.email,
          author_id: book.author_id
        });
        setSubscribed(true);
      } else {
        await axios.post(`${API_BASE}/api/unsubscribe`, {
          user_email: user.email,
          author_id: book.author_id
        });
        setSubscribed(false);
      }
    } catch (err) {
      notify(err.response?.data?.error || "Erreur lors de l'abonnement");
    }
  };

  const handleMessagePublisher = async () => {
    if (!user?.email) {
      notify('créer un compte pour discuter avec cet auteur');
      return;
    }

    let targetEmail = String(publisherEmail || book?.publisher_email || '').trim().toLowerCase();
    if (!targetEmail && Number.isFinite(Number(book?.author_id))) {
      try {
        const profileRes = await axios.get(`${API_BASE}/api/users/${book.author_id}/profile`);
        targetEmail = String(profileRes.data?.email || '').trim().toLowerCase();
        if (targetEmail) setPublisherEmail(targetEmail);
      } catch (_) {
        // keep fallback to alert below
      }
    }

    if (!targetEmail) {
      notify('Auteur indisponible pour la messagerie.');
      return;
    }

    if (String(user.email).trim().toLowerCase() === targetEmail) {
      notify('Vous ne pouvez pas vous envoyer un message.');
      return;
    }

    navigate(`/messages?user=${encodeURIComponent(targetEmail)}`);
  };

  const handleDownload = async () => {
    if (!book?.id || !book?.file_path) return;
    if (Number(book.for_sale) === 1) {
      try {
        const accessRes = await axios.get(`${API_BASE}/api/books/${book.id}/access`, {
          params: { user_email: user?.email }
        });
        if (!accessRes.data?.allowed) {
          const returnTo = encodeURIComponent(window.location.pathname);
          navigate(`/payment?type=book&book_id=${book.id}&return_to=${returnTo}`);
          return;
        }
      } catch (err) {
        notify(err.response?.data?.error || 'Verification acces impossible.');
        return;
      }
    }

    try {
      if (user?.email) {
        await axios.post(`${API_BASE}/api/activity/download`, {
          user_email: user.email,
          book_id: book.id
        });
      }
      window.location.href = `${API_BASE}/${book.file_path}`;
    } catch (err) {
      console.error('Erreur log download:', err);
    }
  };

  const handleOpenBook = async () => {
    if (!book?.id) return;
    if (Number(book.for_sale) === 1) {
      try {
        const accessRes = await axios.get(`${API_BASE}/api/books/${book.id}/access`, {
          params: { user_email: user?.email }
        });
        if (!accessRes.data?.allowed) {
          const returnTo = encodeURIComponent(`/book/${book.id}/read`);
          navigate(`/payment?type=book&book_id=${book.id}&return_to=${returnTo}`);
          return;
        }
      } catch (err) {
        notify(err.response?.data?.error || 'Verification acces impossible.');
        return;
      }
    }

    if (user) {
      try {
        await axios.post(`${API_BASE}/api/activity/read`, {
          user_email: user.email,
          book_id: book.id
        });
      } catch (err) {
        console.error('Erreur log read:', err);
      }
    }
    navigate(`/book/${book.id}/read`);
  };

  if (!book) return <p>Chargement...</p>;

  const displayTitle = book.title;
  const displayAuthor = book.author;
  const publisherName = book.publisher_name || displayAuthor;
  const publisherProfilePath = book.publisher_profile || book.author_profile || 'uploads/profiles/default-profile.png';

  return (
    <div className="book-detail-page">
      <div className={`scroll-down-hint ${showScrollHint ? 'visible' : ''}`}>
        ↓ Defilez vers le bas
      </div>

      <h2>{displayTitle}</h2>

      <div style={{ margin: '12px 0' }}>
        <Ads slot="7085511693" />
      </div>

      <div className="book-detail-main">
        <img
          src={`${API_BASE}/${book.cover}`}
          alt={displayTitle}
          className="book-cover-detail"
        />

        <div className="book-detail-info">
          <div className="author-section">
            <img
              src={`${API_BASE}/${publisherProfilePath}`}
              alt={publisherName}
              className="author-profile"
            />
            <span>{publisherName}</span>
            {user && (
              <button className="subscribe-btn" onClick={handleSubscribe}>
                {subscribed ? 'Se desabonner' : "S'abonner"}
              </button>
            )}
          </div>

          <div className="detail-actions">
            <button onClick={handleLike}>Like {book.likes || 0}</button>
            <button className="share-btn" onClick={handleShare}>Partager</button>
            {user && Number(book.author_id) !== Number(user.id) && (
              <button className="message-btn" onClick={handleMessagePublisher}>
                Message
              </button>
            )}
            {book.file_path && (
              <>
                <a
                  href="#download"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownload();
                  }}
                >
                  Telecharger
                </a>
                <button className="open-book-btn" onClick={handleOpenBook}>
                  Ouvrir le livre
                </button>
              </>
            )}
          </div>

          <div className="comments-section">
            <h4>Commentaires</h4>
            {user && (
              <div className="comment-input">
                <input
                  type="text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Ecrire un commentaire..."
                />
                <button onClick={handleComment}>Envoyer</button>
              </div>
            )}
            <div className="comments-list">
              {comments.map((c, idx) => (
                <div key={idx} className="comment">
                  <img
                    src={toProfileUrl(getCommentAuthorProfile(c))}
                    alt={getCommentAuthorName(c)}
                    className="comment-avatar"
                  />
                  <div className="comment-bubble">
                    <strong className="comment-author">{getCommentAuthorName(c)}</strong>
                    <p className="comment-text">{c.comment}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="views-count">Vues: {book.views || 0}</div>
        </div>
      </div>
    </div>
  );
}


